import { zodResolver } from "@hookform/resolvers/zod";
import { logError } from "@src/appV2/lib/analytics";
import { isValidPhoneNumber } from "libphonenumber-js";
import pluralize from "pluralize";
import { useRef } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

import { useToast } from "../lib/Notifications/Toasts/useToast";
import { useSendWorkerInvitations } from "./api/useSendWorkerInvitations";
import { type WorkerInvitationOpenedFrom } from "./types";
import { sendWorkerInvitationDialogActionedEvent } from "./utils";

const staffMemberSchema = z
  .object({
    name: z.string(),
    phone: z.string(),
    isPrnWorker: z.boolean().optional().default(false),
  })
  .refine((data) => isEmptyRow(data) || isValidRow(data), {
    message: "Both name and valid US phone number are required",
  });

const DEFAULT_STAFF_MEMBERS = [
  {
    name: "",
    phone: "",
    isPrnWorker: false,
  },
  {
    name: "",
    phone: "",
    isPrnWorker: false,
  },
  {
    name: "",
    phone: "",
    isPrnWorker: false,
  },
];

const invitationFormSchema = z.object({
  staffMembers: z.array(staffMemberSchema),
});

export type InvitationFormData = z.infer<typeof invitationFormSchema>;

interface UseWorkerInvitationFormProps {
  openedFrom: WorkerInvitationOpenedFrom;
  isAdmin: boolean;
  workplaceId: string;
  onSuccess: () => void;
}

interface Row {
  name: string;
  phone: string;
  isPrnWorker?: boolean;
}

function isEmptyRow(row: Row): boolean {
  return row.name.trim().length === 0 && row.phone.trim().length === 0;
}

function isValidRow(row: Row): boolean {
  const { hasNameError, hasPhoneError } = getRowValidationIssues(row);
  return !hasNameError && !hasPhoneError;
}

function isValidUsPhoneNumber(phone: string): boolean {
  const digits = phone.trim();
  if (digits.length !== 10) {
    return false;
  }

  return isValidPhoneNumber(digits, "US");
}

function getRowValidationIssues(row: Row): { hasNameError: boolean; hasPhoneError: boolean } {
  return {
    hasNameError: row.name.trim().length === 0,
    hasPhoneError: !isValidUsPhoneNumber(row.phone),
  };
}

function getValidRows(rows: Row[]): Row[] {
  return rows.filter((row) => !isEmptyRow(row)).filter(isValidRow);
}

export function useWorkerInvitationForm({
  onSuccess,
  openedFrom,
  isAdmin,
  workplaceId,
}: UseWorkerInvitationFormProps) {
  const { showSuccessToast, showErrorToast } = useToast();
  const { mutateAsync: sendWorkerInvitations, isLoading: isSendingInvitations } =
    useSendWorkerInvitations({
      onError: (error) => {
        logError("Error sending invitations", { error });
        showErrorToast("Something went wrong. Please try again.");
      },
    });
  const dialogContentRef = useRef<HTMLDivElement>(null);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitted },
  } = useForm<InvitationFormData>({
    resolver: zodResolver(invitationFormSchema),
    defaultValues: {
      staffMembers: DEFAULT_STAFF_MEMBERS,
    },
  });

  const { fields, append } = useFieldArray({
    control,
    name: "staffMembers",
  });

  const watchedStaffMembers = watch("staffMembers");

  const shouldShowFieldError = (index: number, field: "name" | "phone" | "isPrnWorker") => {
    if (!isSubmitted || !errors.staffMembers?.[index]) {
      return false;
    }

    const row = watchedStaffMembers[index];
    if (!row) {
      return false;
    }

    const { hasNameError, hasPhoneError } = getRowValidationIssues(row);

    return field === "name" ? hasNameError : hasPhoneError;
  };

  const addNewStaffMember = () => {
    append({ name: "", phone: "", isPrnWorker: false });

    // Auto-scroll to bottom when adding new row
    setTimeout(() => {
      if (dialogContentRef.current) {
        dialogContentRef.current.scrollTop = dialogContentRef.current.scrollHeight;
      }
    }, 100);
  };

  const onSubmit = async (data: InvitationFormData) => {
    const validStaffMembers = getValidRows(data.staffMembers);
    const validStaffMembersLength = validStaffMembers.length;

    if (validStaffMembersLength > 0) {
      await sendWorkerInvitations({
        workplaceId,
        staffMembers: validStaffMembers,
      });
      showSuccessToast(
        `Thank you for inviting ${validStaffMembersLength} staff ${pluralize(
          "member",
          validStaffMembersLength
        )} to join you on Clipboard. We've sent ${
          validStaffMembersLength === 1 ? "them" : "each of them"
        } an SMS!`
      );
      sendWorkerInvitationDialogActionedEvent({
        workplaceId,
        isAdmin,
        openedFrom,
        action: "SUBMIT",
      });
      onSuccess();
    } else {
      showErrorToast("Something went wrong. Please refresh this page and try again.");
    }
  };

  const onError = () => {
    const { hasNameErrors, hasPhoneErrors } = watchedStaffMembers
      .filter((row) => !isEmptyRow(row))
      .reduce(
        ({ hasNameErrors, hasPhoneErrors }, row) => {
          const { hasNameError, hasPhoneError } = getRowValidationIssues(row);
          return {
            hasNameErrors: hasNameErrors || hasNameError,
            hasPhoneErrors: hasPhoneErrors || hasPhoneError,
          };
        },
        { hasNameErrors: false, hasPhoneErrors: false }
      );

    if (hasNameErrors && hasPhoneErrors) {
      showErrorToast("Please fill in all names and enter valid US phone numbers");
    } else if (hasNameErrors) {
      showErrorToast("Name cannot be empty");
    } else if (hasPhoneErrors) {
      showErrorToast("Please enter valid US phone numbers");
    } else {
      showErrorToast("Please complete all required fields");
    }
  };

  return {
    control,
    fields,
    dialogContentRef,
    shouldShowFieldError,
    addNewStaffMember,
    resetForm: () => {
      reset({
        staffMembers: DEFAULT_STAFF_MEMBERS,
      });
    },
    handleSubmit: handleSubmit(onSubmit, onError),
    hasAtLeastOneValidRow: () => getValidRows(watchedStaffMembers).length > 0,
    isSendingInvitations,
  };
}
